import { UISimulation } from '@/components/help/UISimulation';

const D = 1900;

function Shell({
  step,
  total,
  title,
  children,
}: {
  step: number;
  total: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
          Step {step} of {total}
        </div>
        <div className="text-[11px] font-mono text-dash-secondary">{title}</div>
      </div>
      {children}
    </div>
  );
}

// Brainstorm — 4 frames.
function BSTopic(): JSX.Element {
  return (
    <Shell step={1} total={4} title="Topic input">
      <label className="text-[11px] font-mono uppercase tracking-wide text-dash-muted">
        Topic
      </label>
      <div className="mt-1 rounded border border-dash-border bg-dash-surface px-3 py-2 text-sm text-dash-primary">
        protein timing for runners
      </div>
    </Shell>
  );
}

function BSChips(): JSX.Element {
  return (
    <Shell step={2} total={4} title="AI suggestion chips">
      <div className="flex flex-wrap gap-1.5">
        {[
          'cool-down rituals',
          'race-week tapers',
          'sleep + recovery',
          'mid-run fuel',
          'electrolyte basics',
        ].map((c) => (
          <span
            key={c}
            className="rounded-full border border-accent/40 bg-accent/10 text-accent text-[11px] px-2 py-0.5"
          >
            {c}
          </span>
        ))}
      </div>
    </Shell>
  );
}

function BSGenerating(): JSX.Element {
  return (
    <Shell step={3} total={4} title="Generating briefs">
      <div className="text-[10px] font-mono uppercase text-warning">
        claude is writing 4 briefs…
      </div>
      <div className="mt-2 space-y-1.5">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-2 rounded bg-dash-surface overflow-hidden"
          >
            <div
              className="h-full bg-warning/60"
              style={{ width: `${i * 33}%` }}
            />
          </div>
        ))}
      </div>
    </Shell>
  );
}

function BSCards(): JSX.Element {
  return (
    <Shell step={4} total={4} title="Briefs proposed">
      <ul className="space-y-2 text-xs">
        {[
          'When to take protein after a long run',
          'Carbs vs protein in the recovery window',
          'Plant-based options that hit 20g',
        ].map((t) => (
          <li
            key={t}
            className="rounded border border-dash-border bg-dash-surface px-3 py-1.5"
          >
            <div className="text-dash-primary text-sm">{t}</div>
            <div className="text-[10px] font-mono uppercase text-dash-muted mt-0.5">
              how-to · 950 words
            </div>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

export function BrainstormSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <BSTopic />, label: 'Type a topic', duration: D },
        { frame: <BSChips />, label: 'AI suggests sub-angles', duration: D },
        { frame: <BSGenerating />, label: 'Claude writes briefs', duration: D },
        { frame: <BSCards />, label: 'Three cards land', duration: D },
      ]}
    />
  );
}

// Approving briefs — 3 frames.
function ABRead(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Read the brief">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="text-dash-primary text-sm">
          When to take protein after a long run
        </div>
        <div className="text-[10px] font-mono uppercase text-dash-muted mt-1">
          how-to · 950 words
        </div>
        <ul className="mt-2 space-y-0.5 text-dash-secondary list-disc list-inside">
          <li>Recovery window science</li>
          <li>Hitting 20g without bloating</li>
          <li>Common mistakes</li>
        </ul>
      </div>
    </Shell>
  );
}

function ABApprove(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Click Approve">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="flex justify-between items-center">
          <div className="text-dash-primary text-sm">
            When to take protein after a long run
          </div>
          <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5 text-[11px]">
            Approve
          </span>
        </div>
      </div>
    </Shell>
  );
}

function ABRun(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Pipeline starts">
      <div className="rounded border border-warning/40 bg-warning/10 p-3 text-xs">
        <div className="flex justify-between items-center">
          <div className="text-dash-primary text-sm">
            When to take protein after a long run
          </div>
          <span className="text-[10px] font-mono uppercase text-warning">
            writing · iter 1
          </span>
        </div>
      </div>
    </Shell>
  );
}

export function ApproveBriefSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <ABRead />, label: 'Read the brief', duration: D },
        { frame: <ABApprove />, label: 'Click Approve', duration: D },
        { frame: <ABRun />, label: 'Run enters the pipeline', duration: D },
      ]}
    />
  );
}

// Pipeline monitoring — 3 frames.
function PMWrite(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Writing">
      <div className="rounded border border-warning/40 bg-warning/10 p-3 text-xs">
        <div className="flex justify-between items-center">
          <div className="text-dash-primary text-sm">Protein timing for runners</div>
          <span className="text-[10px] font-mono uppercase text-warning">
            writing
          </span>
        </div>
        <div className="mt-1 text-dash-secondary">iteration 1 of 3</div>
      </div>
    </Shell>
  );
}

function PMReview(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Reviewing">
      <div className="rounded border border-accent/40 bg-accent/10 p-3 text-xs">
        <div className="flex justify-between items-center">
          <div className="text-dash-primary text-sm">Protein timing for runners</div>
          <span className="text-[10px] font-mono uppercase text-accent">
            reviewing
          </span>
        </div>
        <div className="mt-1 text-dash-secondary">score: 7.6 / 10 — revising</div>
      </div>
    </Shell>
  );
}

function PMDraft(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Draft">
      <div className="rounded border border-success/40 bg-success/10 p-3 text-xs">
        <div className="flex justify-between items-center">
          <div className="text-dash-primary text-sm">Protein timing for runners</div>
          <span className="rounded bg-success/15 text-success border border-success/40 px-2 py-0.5 text-[10px] uppercase font-mono">
            draft · 8.7
          </span>
        </div>
      </div>
    </Shell>
  );
}

export function PipelineMonitorSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <PMWrite />, label: 'Writing iteration 1', duration: D },
        { frame: <PMReview />, label: 'Reviewer asks for changes', duration: D },
        { frame: <PMDraft />, label: 'Draft lands ready for review', duration: D },
      ]}
    />
  );
}

// Cancel / Force-complete — 4 frames.
function CFActive(): JSX.Element {
  return (
    <Shell step={1} total={4} title="Active run">
      <div className="rounded border border-warning/40 bg-warning/10 p-3 text-xs">
        <div className="flex justify-between items-center">
          <div className="text-dash-primary text-sm">Stuck article</div>
          <span className="text-[10px] font-mono uppercase text-warning">
            writing · iter 2
          </span>
        </div>
        <div className="mt-2 flex gap-2">
          <span className="rounded bg-error/15 text-error border border-error/40 px-2 py-0.5 text-[11px]">
            Cancel
          </span>
          <span className="rounded bg-warning/15 text-warning border border-warning/40 px-2 py-0.5 text-[11px]">
            Force complete
          </span>
        </div>
      </div>
    </Shell>
  );
}

function CFCancel(): JSX.Element {
  return (
    <Shell step={2} total={4} title="Cancel">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="text-dash-primary">Cancel this pipeline run?</div>
        <div className="mt-2 flex gap-2">
          <span className="rounded border border-dash-border text-dash-secondary px-2 py-0.5">
            Keep going
          </span>
          <span className="rounded bg-error/15 text-error border border-error/40 px-2 py-0.5">
            Cancel
          </span>
        </div>
      </div>
    </Shell>
  );
}

function CFForce(): JSX.Element {
  return (
    <Shell step={3} total={4} title="Force complete">
      <div className="rounded border border-warning/40 bg-warning/10 p-3 text-xs">
        <div className="text-warning font-mono text-[10px] uppercase">
          Admin only
        </div>
        <div className="mt-1 text-dash-primary">
          Push the current write output to drafts without finishing review?
        </div>
        <pre className="mt-2 max-h-16 overflow-hidden rounded border border-dash-border bg-dash-bg p-2 text-[10px] text-dash-secondary">
          # When to refuel{'\n'}Protein within 30 minutes is...
        </pre>
      </div>
    </Shell>
  );
}

function CFDone(): JSX.Element {
  return (
    <Shell step={4} total={4} title="Forced">
      <div className="rounded border border-success/40 bg-success/10 p-3 text-xs">
        <div className="text-dash-primary">Forced to draft.</div>
        <div className="mt-1 text-[10px] font-mono uppercase text-warning">
          flag: force_completed
        </div>
      </div>
    </Shell>
  );
}

export function CancelForceSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <CFActive />, label: 'Active run controls', duration: D },
        { frame: <CFCancel />, label: 'Cancel discards the run', duration: D },
        { frame: <CFForce />, label: 'Force complete previews output', duration: D },
        { frame: <CFDone />, label: 'Lands as a flagged draft', duration: D },
      ]}
    />
  );
}
